/**
 * VehicleAssignments Component
 * Planning-level vehicle roster: each entry defines a vehicle/driver slot
 * that segments can reference via vehicleAssignmentId.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bus, CarFront, Car, Plane, Train, Ship, Bike, Footprints, Pencil, Trash2, Check, X, User, Users2 } from 'lucide-react';
import {
  VehicleAssignment,
  VehicleType,
  VehicleInfo,
  TransportationInfo,
  TravelerInfo,
  PartnerDriverInfo,
  PartnerInfo,
  DriverType
} from '../../types/matchdayPlanning';

interface VehicleAssignmentsProps {
  assignments: VehicleAssignment[];
  transportations: TransportationInfo[];
  travelers: TravelerInfo[];
  partnerDrivers: PartnerDriverInfo[];
  partners: PartnerInfo[];
  onAdd: (assignment: Partial<VehicleAssignment>) => void;
  onUpdate: (id: number, assignment: Partial<VehicleAssignment>) => void;
  onDelete: (id: number) => void;
  isReadOnly?: boolean;
}

const VEHICLE_TYPE_ICON_COMPONENTS: Record<VehicleType, React.ReactNode> = {
  bus: <Bus size={14} />,
  minibus: <CarFront size={14} />,
  car: <Car size={14} />,
  plane: <Plane size={14} />,
  train: <Train size={14} />,
  taxi: <Car size={14} />,
  rentalcar: <CarFront size={14} />,
  ship: <Ship size={14} />,
  bicycle: <Bike size={14} />,
  onfoot: <Footprints size={14} />
};

const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  bus: 'Mannschaftsbus',
  minibus: 'Transferbus',
  car: 'KitCar',
  plane: 'Flugzeug',
  train: 'Bahn',
  taxi: 'Taxi',
  rentalcar: 'Mietwagen',
  ship: 'Schiff',
  bicycle: 'Fahrrad',
  onfoot: 'Zu Fuß'
};

const VehicleAssignments: React.FC<VehicleAssignmentsProps> = ({
  assignments,
  transportations,
  travelers,
  partnerDrivers,
  partners,
  onAdd,
  onUpdate,
  onDelete,
  isReadOnly = false
}) => {
  const { t } = useTranslation(['common']);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<VehicleAssignment>>({
    vehicleType: 'bus',
    displayOrder: (assignments.length || 0) + 1
  });

  const handleAddClick = () => {
    setFormData({
      vehicleType: 'bus',
      displayOrder: (assignments.length || 0) + 1
    });
    setIsAdding(true);
    setEditingId(null);
  };

  const handleEditClick = (assignment: VehicleAssignment) => {
    setFormData({
      vehicleType: assignment.vehicleType,
      vehicleId: assignment.vehicleId,
      transportationId: assignment.transportationId,
      partnerId: assignment.partnerId,
      travelerId: assignment.travelerId,
      partnerDriverId: assignment.partnerDriverId,
      driverType: assignment.driverType,
      notes: assignment.notes,
      displayOrder: assignment.displayOrder,
      weeklyDrivingHoursOffset: assignment.weeklyDrivingHoursOffset
    });
    setEditingId(assignment.id);
    setIsAdding(false);
  };

  const handleSave = () => {
    if (isAdding) {
      onAdd(formData);
    } else if (editingId !== null) {
      onUpdate(editingId, formData);
    }
    setIsAdding(false);
    setEditingId(null);
    setFormData({ vehicleType: 'bus' });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ vehicleType: 'bus' });
  };

  const getVehicleDisplayName = (assignment: VehicleAssignment): string => {
    if (assignment.vehicle) return assignment.vehicle.name;
    if (assignment.transportation) return assignment.transportation.name;
    if (assignment.vehicleId) {
      const t = transportations.find(t => t.id === assignment.vehicleId);
      if (t) return t.name;
    }
    if (assignment.transportationId) {
      const t = transportations.find(t => t.id === assignment.transportationId);
      if (t) return t.name;
    }
    return '-';
  };

  const getDriverDisplayName = (assignment: VehicleAssignment): string => {
    if (assignment.driverType === 'traveler') {
      if (assignment.traveler) return assignment.traveler.fullName;
      if (assignment.travelerId) {
        const d = travelers.find(t => t.id === assignment.travelerId);
        if (d) return d.fullName;
      }
    } else if (assignment.driverType === 'partnerDriver') {
      if (assignment.partnerDriver) return assignment.partnerDriver.fullName;
      if (assignment.partnerDriverId) {
        const d = partnerDrivers.find(p => p.id === assignment.partnerDriverId);
        if (d) return d.fullName;
      }
    }
    // Fallback: check both
    if (assignment.traveler) return assignment.traveler.fullName;
    if (assignment.partnerDriver) return assignment.partnerDriver.fullName;
    return '-';
  };

  const getPartnerDisplayName = (assignment: VehicleAssignment): string => {
    if (assignment.partner) return assignment.partner.name;
    if (assignment.partnerId) {
      const p = partners.find(p => p.id === assignment.partnerId);
      if (p) return p.name;
    }
    return '';
  };

  const sectionStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#424242',
    whiteSpace: 'nowrap'
  };

  const assignmentsRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    flex: 1
  };

  const assignmentChipStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    padding: '6px 12px',
    fontSize: '0.85rem'
  };

  const chipTextStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const chipActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
    marginLeft: '4px'
  };

  const smallBtnStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    fontSize: '0.8rem',
    borderRadius: '4px'
  };

  const emptyTextStyle: React.CSSProperties = {
    color: '#9e9e9e',
    fontStyle: 'italic',
    fontSize: '0.85rem'
  };

  return (
    <div style={sectionStyle}>
      <div style={headerStyle}>
        <h4 style={titleStyle}><Bus size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> {t('common:planning.vehicleAssignments', 'Fahrzeug-Zuordnungen')}</h4>
        {!isReadOnly && (
          <button type="button" className="btn btn-sm btn-primary" onClick={handleAddClick} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
            + {t('common:planning.addVehicle', 'Fahrzeug hinzufügen')}
          </button>
        )}
      </div>

      <div style={assignmentsRowStyle}>
        {assignments.length === 0 && !isAdding && (
          <span style={emptyTextStyle}>{t('common:planning.noVehiclesAssigned', 'Noch keine Fahrzeuge zugeordnet')}</span>
        )}

        {assignments.map((assignment) => (
          <div key={assignment.id}>
            {editingId === assignment.id ? (
              <div className="assignment-card editing" style={{ background: '#fff', border: '1px solid #1976d2', borderRadius: '8px', padding: '12px' }}>
                <AssignmentForm
                  formData={formData}
                  setFormData={setFormData}
                  transportations={transportations}
                  travelers={travelers}
                  partnerDrivers={partnerDrivers}
                  partners={partners}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              </div>
            ) : (
              <div style={assignmentChipStyle}>
                <span style={chipTextStyle}>
                  <strong>{VEHICLE_TYPE_ICON_COMPONENTS[assignment.vehicleType as VehicleType] || <Bus size={14} />} {getVehicleDisplayName(assignment)}</strong>
                  <span style={{ color: '#666' }}>{getDriverDisplayName(assignment)}</span>
                  {getPartnerDisplayName(assignment) && (
                    <span style={{ color: '#888' }}><Users2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> {getPartnerDisplayName(assignment)}</span>
                  )}
                </span>
                {!isReadOnly && (
                  <span style={chipActionsStyle}>
                    <button type="button" style={smallBtnStyle} onClick={() => handleEditClick(assignment)} title="Bearbeiten"><Pencil size={12} /></button>
                    <button type="button" style={smallBtnStyle} onClick={() => onDelete(assignment.id)} title="Löschen"><Trash2 size={12} /></button>
                  </span>
                )}
              </div>
            )}
          </div>
        ))}

        {isAdding && (
          <div className="assignment-card adding" style={{ background: '#fff', border: '1px solid #1976d2', borderRadius: '8px', padding: '12px' }}>
            <AssignmentForm
              formData={formData}
              setFormData={setFormData}
              transportations={transportations}
              travelers={travelers}
              partnerDrivers={partnerDrivers}
              partners={partners}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface AssignmentFormProps {
  formData: Partial<VehicleAssignment>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<VehicleAssignment>>>;
  transportations: TransportationInfo[];
  travelers: TravelerInfo[];
  partnerDrivers: PartnerDriverInfo[];
  partners: PartnerInfo[];
  onSave: () => void;
  onCancel: () => void;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({
  formData,
  setFormData,
  transportations,
  travelers,
  partnerDrivers,
  partners,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation(['common']);

  const getLicenseClassesArray = (classes: string | string[] | undefined | null): string[] => {
    if (!classes) return [];
    if (Array.isArray(classes)) return classes;
    return classes.split(',').map(c => c.trim()).filter(c => c);
  };

  const filteredTravelers = travelers.filter(d => getLicenseClassesArray(d.licenseClasses).length > 0);

  // Map VehicleType to Transportation types for filtering
  const getTransportationTypesForVehicle = (vt: VehicleType | string | undefined): string[] => {
    switch (vt) {
      case 'bus': return ['bus'];
      case 'minibus': return ['bus', 'car'];
      case 'car': return ['car'];
      case 'plane': return ['plane'];
      default: return [];
    }
  };

  const filteredTransportations = transportations.filter(tr => {
    const allowedTypes = getTransportationTypesForVehicle(formData.vehicleType);
    return allowedTypes.length === 0 || allowedTypes.includes(tr.type);
  });

  const driverType: DriverType = formData.driverType || 'traveler';

  const handleDriverTypeChange = (type: DriverType) => {
    setFormData(prev => ({
      ...prev,
      driverType: type,
      travelerId: undefined,
      partnerDriverId: undefined
    }));
  };

  return (
    <div className="assignment-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="vehicleType">{t('common:planning.vehicleType', 'Fahrzeugtyp')}</label>
          <select
            id="vehicleType"
            value={formData.vehicleType || 'bus'}
            onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value as VehicleType, transportationId: undefined })}
          >
            {Object.entries(VEHICLE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="transportation">{t('common:planning.vehicle', 'Fahrzeug (Transportation)')}</label>
          <select
            id="transportation"
            value={formData.transportationId || ''}
            onChange={(e) => setFormData({ ...formData, transportationId: e.target.value ? parseInt(e.target.value) : undefined })}
          >
            <option value="">-- Kein Fahrzeug --</option>
            {filteredTransportations.map(tr => (
              <option key={tr.id} value={tr.id}>{tr.name} ({tr.type})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="partner">{t('common:planning.partner', 'Partner')}</label>
          <select
            id="partner"
            value={formData.partnerId || ''}
            onChange={(e) => setFormData({ ...formData, partnerId: e.target.value ? parseInt(e.target.value) : undefined })}
          >
            <option value="">-- Kein Partner --</option>
            {partners.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>{t('common:planning.driverType', 'Fahrertyp')}</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="driverType"
                value="traveler"
                checked={driverType === 'traveler'}
                onChange={() => handleDriverTypeChange('traveler')}
              />
              <User size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Staff
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="driverType"
                value="partnerDriver"
                checked={driverType === 'partnerDriver'}
                onChange={() => handleDriverTypeChange('partnerDriver')}
              />
              <Users2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Partnerfahrer
            </label>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="driver">{t('common:planning.driver', 'Fahrer')}</label>
          {driverType === 'traveler' ? (
            <select
              id="driver"
              value={formData.travelerId || ''}
              onChange={(e) => setFormData({ ...formData, travelerId: e.target.value ? parseInt(e.target.value) : undefined, driverType: 'traveler' })}
            >
              <option value="">-- Kein Fahrer --</option>
              {filteredTravelers.map(d => {
                const classes = getLicenseClassesArray(d.licenseClasses);
                return (
                  <option key={d.id} value={d.id}>
                    {d.fullName} {classes.length ? `(${classes.join(', ')})` : ''}
                  </option>
                );
              })}
            </select>
          ) : (
            <select
              id="partnerDriver"
              title="Partnerfahrer auswählen"
              value={formData.partnerDriverId || ''}
              onChange={(e) => setFormData({ ...formData, partnerDriverId: e.target.value ? parseInt(e.target.value) : undefined, driverType: 'partnerDriver' })}
            >
              <option value="">-- Kein Partnerfahrer --</option>
              {partnerDrivers.map(d => (
                <option key={d.id} value={d.id}>
                  {d.fullName} {d.partnerName ? `(${d.partnerName})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="weeklyOffset">Wochenlenkzeit-Vorlauf (h)</label>
          <input
            id="weeklyOffset"
            type="number"
            min="0"
            max="56"
            step="0.5"
            value={formData.weeklyDrivingHoursOffset ?? 0}
            onChange={(e) => setFormData({ ...formData, weeklyDrivingHoursOffset: parseFloat(e.target.value) || 0 })}
            placeholder="0"
            title="Bereits aufgelaufene Wochenlenkzeit in Stunden"
          />
        </div>
        <div className="form-group">
          <label htmlFor="notes">{t('common:planning.notes', 'Notizen')}</label>
          <input
            id="notes"
            type="text"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Optionale Notizen..."
          />
        </div>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={onSave}>
          <Check size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {t('common:save', 'Speichern')}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          <X size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {t('common:cancel', 'Abbrechen')}
        </button>
      </div>
    </div>
  );
};

export default VehicleAssignments;
